<script setup lang="ts">
import { watch, computed } from 'vue';

import {
  useBrowserStore,
  useStatsigEventsStore,
} from '@apps/main/new-desktop/core/stores';

import { AppButton, AppRawButton } from '@libs/ui';

import type { FerryProductCardData, FerryUpsellType } from '../../../models';
import { useFerryTicketCategoryUpsellStore } from '../../../stores';
import TripOverviewTrashIconButton from '../../TripOverviewTrashIconButton.vue';
import DateCardTag from '../DateCardTag.vue';

import FerryCardHeader from './FerryCardHeader.vue';
import FerryCardRouteDetails from './FerryCardRouteDetails.vue';
import FerryCardTags from './FerryCardTags.vue';
import FerryCardFerryDetails from './FerryCardFerryDetails.vue';
import FerryCardUpsellLoader from './FerryCardUpsellLoader.vue';
import FerryCardUpsell from './FerryCardUpsell.vue';

interface FerryProductCardContentProps {
  productCardContent: FerryProductCardData;
  printMode?: boolean;
  shouldHideProductLink: boolean;
}

const props = defineProps<FerryProductCardContentProps>();

interface FerryProductCardContentEmits {
  (event: 'change-upsell', upsellType: FerryUpsellType): void;
  (event: 'show-details'): void;
  (event: 'change-ferry'): void;
  (event: 'remove-ferry'): void;
}

const emit = defineEmits<FerryProductCardContentEmits>();

const browserStore = useBrowserStore();
const ferryTicketCategoryUpsellStore = useFerryTicketCategoryUpsellStore();
const statsigEventsStore = useStatsigEventsStore();

function onContentClick(): void {
  emit('show-details');
}

const isLoading = computed<boolean>(() => {
  if (props.productCardContent.upsell.ingredientId) {
    return ferryTicketCategoryUpsellStore.isLoadingDictionary.has(
      props.productCardContent.upsell.ingredientId
    );
  }

  return false;
});

const allTickets = computed(() =>
  ferryTicketCategoryUpsellStore.getTicketCategories(
    props.productCardContent.upsell.token
  )
);

const baseTicketPrice = computed<number>(() => {
  // FYI: For initial display of ticket category prices we need to use base price as seleted price is 0.
  // In new flow, we are no longer comparing to base price as old flow was showing relative price only for flexi tickets.
  // New flow needs to show both relative prices
  if (
    props.productCardContent.upsell.ticket.price !== undefined &&
    props.productCardContent.upsell.ticket.basePrice !== undefined
  ) {
    return props.productCardContent.upsell.ticket.price === 0
      ? props.productCardContent.upsell.ticket.basePrice
      : props.productCardContent.upsell.ticket.price;
  }
  return 0;
});

watch(
  props,
  () => {
    ferryTicketCategoryUpsellStore.fetchTicketCategories(
      props.productCardContent.upsell.token,
      props.productCardContent.upsell.ingredientId
    );
  },
  { immediate: true }
);

function selectTicketCategory(category: string): void {
  ferryTicketCategoryUpsellStore.currentFerryTicketCategoryEditionKey =
    props.productCardContent.upsell.token;
  ferryTicketCategoryUpsellStore.currentFerryTicketEditionIngredientId =
    props.productCardContent.upsell.ingredientId;
  ferryTicketCategoryUpsellStore.setUserSelectionFromModal(category);
}

function removeFerry(): void {
  statsigEventsStore.logExperimentIngredientRemoved('transport');
  emit('remove-ferry');
}
</script>

<template>
  <div class="ferry-product-card-content">
    <div class="ferry-content">
      <FerryCardHeader
        :logo-urls="props.productCardContent.details.logo.urls"
        :from-port-label="props.productCardContent.fromPort.country"
        :to-port-label="props.productCardContent.toPort.country"
        :should-hide-product-link="props.shouldHideProductLink"
        @show-details="onContentClick"
      />

      <div class="middle-section">
        <FerryCardRouteDetails :content="props.productCardContent" />

        <FerryCardTags
          :warning-tags="props.productCardContent.details.warningTags"
        />

        <FerryCardFerryDetails
          :vehicles="props.productCardContent.upsell.vehicles"
          :allow-vehicles="props.productCardContent.upsell.allowVehicles"
          :cabin="props.productCardContent.upsell.cabin"
          :print-mode="props.printMode"
          @change-upsell="emit('change-upsell', $event)"
        />
      </div>

      <div class="upsell-section">
        <FerryCardUpsellLoader v-if="isLoading" />

        <FerryCardUpsell
          v-else
          :base-ticket-price="baseTicketPrice"
          :tickets="allTickets"
          :selected-ticket-type="props.productCardContent.upsell.ticket.type"
          :print-mode="props.printMode"
          @select-ticket-category="selectTicketCategory"
        />
      </div>

      <!-- button -->
      <div
        v-if="props.shouldHideProductLink && !printMode"
        class="buttons-section"
      >
        <AppRawButton class="details" @click="emit('show-details')">
          {{ $t('planner.details') }}
        </AppRawButton>

        <AppButton
          state="enabled"
          type="primary"
          size="small"
          :ariaLabel="$t('a11y.change_ferry')"
          @click="emit('change-ferry')"
        >
          {{ $t('buttons.ferry') }}
        </AppButton>
      </div>
    </div>
    <DateCardTag :date="props.productCardContent.details.date" />
    <div
      v-if="props.shouldHideProductLink && !props.printMode"
      class="remove-ferry"
    >
      <TripOverviewTrashIconButton
        :with-border="browserStore.isSmallerThan600px"
        :rounded="true"
        :ariaLabel="$t('a11y.remove_ferry')"
        @remove="removeFerry"
      />
    </div>
  </div>
</template>

<style scoped lang="scss">
.ferry-product-card-content {
  --trash-icon-spacing: var(--spacing-16);

  @include for-less-or-equal-than-599-px {
    --trash-icon-spacing: 0;
  }

  padding: var(--spacing-16);
  position: relative;

  .ferry-content {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    align-items: flex-start;

    @include for-less-or-equal-than-599-px {
      gap: var(--spacing-16);
    }

    .middle-section {
      flex: 1;
      display: flex;
      flex-direction: column;
      padding-bottom: var(--spacing-12);
      gap: var(--spacing-12);
      width: 100%;
    }

    .upsell-section {
      border-top: 1px dashed var(--color-overlay);
      padding-top: var(--spacing-12);
      padding-bottom: var(--spacing-8);
      align-self: stretch;
      width: 100%;
      display: flex;
      align-items: center;
    }

    .buttons-section {
      border-top: 1px dashed var(--color-overlay);
      padding-top: var(--spacing-12);
      align-self: stretch;
      width: 100%;
      display: flex;
      align-items: center;
      justify-content: flex-end;
      gap: var(--spacing-16);
      .details {
        display: flex;
        @include font-label-m-light;
        text-decoration: underline;
        cursor: pointer;
      }
    }
  }

  .remove-ferry {
    position: absolute;
    top: var(--trash-icon-spacing);
    right: var(--trash-icon-spacing);
  }
}
</style>
